import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Users, Phone, AlertTriangle, Flame, FileText, Handshake, TrendingUp, Loader2, UserCheck, ArrowRight, Crown } from "lucide-react";
import { fmtNum } from "@/lib/format";

// Pagina Team Performance: cruscotto confronto agenti per l'admin/leader.
// Ogni riga = un agente con i suoi KPI. Click su una riga → Dashboard "Vista come" di quell'agente.
export default function TeamPerformancePage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("totalAssigned");   // sort column
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    api.get("/dashboard/team")
      .then((r) => setMembers(r.data.members || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  const sortKey = (m) => {
    const kpi = m.kpi || {};
    if (sortBy === "name") return (m.name || m.email || "").toLowerCase();
    return kpi[sortBy] || 0;
  };
  const sorted = [...members].sort((a, b) => {
    const va = sortKey(a), vb = sortKey(b);
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const onSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const total = members.reduce((acc, m) => {
    const kpi = m.kpi || {};
    acc.totalAssigned += kpi.totalAssigned || 0;
    acc.hot += kpi.hot || 0;
    acc.todayCalls += kpi.todayCalls || 0;
    acc.expiredRecalls += kpi.expiredRecalls || 0;
    acc.liquidated += kpi.liquidatedLast30d || 0;
    acc.attesaDoc += kpi.attesaDoc || 0;
    acc.inTrattativa += kpi.inTrattativa || 0;
    return acc;
  }, { totalAssigned: 0, hot: 0, todayCalls: 0, expiredRecalls: 0, liquidated: 0, attesaDoc: 0, inTrattativa: 0 });

  return (
    <AppLayout
      title="Confronto Team"
      subtitle="Performance comparata degli agenti"
    >
      {/* Totals summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <TotalCard label="Clienti gestiti" value={total.totalAssigned} color="#0A192F" icon={Users} />
        <TotalCard label="Lead caldi" value={total.hot} color="#B7955B" icon={Flame} />
        <TotalCard label="Richiami oggi" value={total.todayCalls} color="#0891b2" icon={Phone} />
        <TotalCard label="Liquidati 30gg" value={total.liquidated} color="#16a34a" icon={TrendingUp} />
      </div>

      {/* Comparison table */}
      <div className="bg-white border border-slate-200 rounded-[3px] overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3 bg-slate-50 flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#B7955B]" />
          <h2 className="font-serif text-base text-slate-900">Classifica del team</h2>
          <span className="text-xs text-slate-400 ml-auto">Click su un agente per vedere la sua dashboard</span>
        </div>
        {loading ? (
          <div className="flex justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">Nessun agente nel team.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-performance-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <Th col="name" onSort={onSort} sortBy={sortBy} sortDir={sortDir}>Agente</Th>
                  <Th col="totalAssigned" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Clienti</Th>
                  <Th col="todayCalls" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Oggi</Th>
                  <Th col="expiredRecalls" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Scaduti</Th>
                  <Th col="hot" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Caldi</Th>
                  <Th col="attesaDoc" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Attesa Doc</Th>
                  <Th col="inTrattativa" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Trattativa</Th>
                  <Th col="liquidatedLast30d" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Liq 30gg</Th>
                  <Th col="active7d" onSort={onSort} sortBy={sortBy} sortDir={sortDir} right>Attivi 7gg</Th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m, idx) => {
                  const kpi = m.kpi || {};
                  const isTop = idx === 0 && kpi.liquidatedLast30d > 0;
                  return (
                    <tr key={m.id}
                        className="border-b border-[#F0F2F5] hover:bg-slate-50/50 transition group"
                        data-testid={`team-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {isTop && <Crown className="w-3.5 h-3.5 text-[#B7955B]" />}
                          <div className="w-8 h-8 rounded-full bg-[#0A192F] text-white text-xs flex items-center justify-center font-semibold shrink-0">
                            {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{m.name || "—"}</div>
                            <div className="text-[10px] text-slate-500 truncate">{m.email}</div>
                          </div>
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] bg-slate-100 text-slate-600">{m.role}</span>
                        </div>
                      </td>
                      <Td value={kpi.totalAssigned} />
                      <Td value={kpi.todayCalls} accent="#0891b2" />
                      <Td value={kpi.expiredRecalls} accent={kpi.expiredRecalls > 0 ? "#dc2626" : undefined} />
                      <Td value={kpi.hot} accent="#B7955B" />
                      <Td value={kpi.attesaDoc} />
                      <Td value={kpi.inTrattativa} />
                      <Td value={kpi.liquidatedLast30d} accent="#16a34a" bold />
                      <Td value={kpi.active7d} />
                      <td className="px-3 py-3 text-right">
                        <Link to={`/?viewAs=${m.id}`} data-testid={`view-as-${m.id}`}
                              className="text-xs text-[#B7955B] opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 transition">
                          Apri <ArrowRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-slate-400 mt-3 italic flex items-start gap-1.5">
        <UserCheck className="w-3 h-3 mt-0.5 shrink-0" />
        <span>I dati mostrati sono filtrati per ciascun agente secondo RBAC. Cliccando su una riga apri la dashboard personale di quell'agente (impersonation read-only).</span>
      </div>
    </AppLayout>
  );
}

function TotalCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[3px] p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-[3px] flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="text-2xl font-serif tabular-nums" style={{ color }}>{fmtNum(value)}</div>
      </div>
    </div>
  );
}

function Th({ col, sortBy, sortDir, onSort, right, children }) {
  const active = sortBy === col;
  return (
    <th className={`px-3 py-2.5 ${right ? "text-right" : "text-left"} cursor-pointer select-none hover:text-slate-800 ${active ? "text-[#0A192F]" : ""}`}
        onClick={() => onSort(col)}>
      {children} {active && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );
}

function Td({ value, accent, bold }) {
  const v = value || 0;
  return (
    <td className={`px-3 py-3 text-right tabular-nums ${bold ? "font-semibold" : ""}`}
        style={{ color: accent && v > 0 ? accent : undefined }}>
      {v > 0 ? fmtNum(v) : <span className="text-slate-300">—</span>}
    </td>
  );
}
